import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { ScrollReveal } from '@podus/ui';

const features = [
    {
        number: '01',
        title: 'One Account. Every Legal Solution.',
        description:
            'Access all Podus services with a single secure login. Podus brings together legal guidance, lawyer consultations, dispute resolution, contract tools, background checks, and legal aid under one unified account. No switching platforms. No repeated sign-ins. Just one seamless legal ecosystem built around your needs.',
        image: '/services/podus_feature_1.svg',
    },
    {
        number: '02',
        title: 'AI-Powered Legal Intelligence',
        description:
            "Instant clarity before you speak to a lawyer. With Podus AI, users receive real-time legal guidance, document drafting support, and scenario analysis. Whether you're facing a dispute, reviewing a contract, or trying to understand your rights, Podus AI simplifies complex legal information into clear, actionable insights.",
        image: '/services/podus_feature_2.svg',
    },
    {
        number: '03',
        title: 'Connect With Real Lawyers — On Demand',
        description:
            'From AI advice to human expertise in minutes. Book virtual appointments, request contract reviews, or get matched with volunteer legal aid lawyers directly within the app. Podus bridges the gap between digital intelligence and professional legal support — securely and efficiently.',
        image: '/services/podus_feature_3.svg',
    },
    {
        number: '04',
        title: 'Smart Contract & Obligation Tracking',
        description:
            'Understand what you signed — and never miss a deadline. Podus Contract analyzes agreements, highlights risks, explains obligations, and syncs important dates to your calendar. From freelancers to SMEs, users gain confidence in every document they sign and every commitment they make.',
        image: '/services/podus_feature_4.svg',
    },
    {
        number: '05',
        title: 'Court-Connected Dispute Resolution',
        description:
            'File, track, and resolve disputes digitally. Through Podus Multidoor, users can submit cases directly to Multi-Door Courts for mediation, arbitration, or reconciliation. The platform simplifies case intake, handles payments, and keeps users updated — reducing court congestion while increasing access.',
        image: '/services/podus_feature_5.svg',
    },
    {
        number: '06',
        title: 'Secure & Trusted Legal Infrastructure',
        description:
            'Built for justice. Designed for protection. Podus operates with strong data protection standards, encrypted communication, role-based access control, and full audit trails. From sensitive case filings to background checks, every interaction is protected with privacy and accountability at its core.',
        image: '/services/podus_feature_6.svg',
    },
];

/** Mobile accordion list matching the numbered-list design */
function MobileFeatureAccordion() {
    const { t } = useLanguage();
    const [activeIndex, setActiveIndex] = useState(0);

    return (
        <div className="flex flex-col">
            {features.map((feature, i) => {
                const isActive = activeIndex === i;
                return (
                    <button
                        key={i}
                        type="button"
                        className="flex items-start gap-5 w-full text-left"
                        onClick={() => setActiveIndex(i)}
                    >
                        <span
                            className={`text-base font-bold min-w-[32px] text-right pt-5 transition-colors duration-300 ${
                                isActive ? 'text-secondary' : 'text-secondary-100'
                            }`}
                        >
                            {feature.number}
                        </span>

                        <div
                            className={`flex-1 border-l-2 pl-5 py-5 transition-colors duration-300 ${
                                isActive ? 'border-secondary' : 'border-gray-200'
                            }`}
                        >
                            <h3
                                className={`text-2xl font-bold transition-colors duration-300 ${
                                    isActive ? 'text-secondary' : 'text-[#AEAEAE]'
                                }`}
                            >
                                {t(`podusFeatures.${i}.title`)}
                            </h3>

                            <div
                                className="grid"
                                style={{
                                    gridTemplateRows: isActive ? '1fr' : '0fr',
                                    transition: 'grid-template-rows 0.4s ease-out',
                                }}
                            >
                                <div className="overflow-hidden">
                                    <p className="text-secondary-300 text-sm leading-[2] mt-3">
                                        {t(`podusFeatures.${i}.description`)}
                                    </p>
                                    <img
                                        src={feature.image}
                                        alt={t(`podusFeatures.${i}.title`)}
                                        className="mt-5 w-full max-h-48 object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

export default function PodusFeatures() {
    const { t } = useLanguage();
    const [activeIndex, setActiveIndex] = useState(0);

    const toggle = (i: number) => setActiveIndex(activeIndex === i ? -1 : i);

    const activeFeature = activeIndex >= 0 ? features[activeIndex] : features[0];

    return (
        <>
            {/* ─── Desktop: Click-Driven Feature Accordion ─── */}
            <section className="hidden lg:block px-16 xl:px-20 py-16 xl:py-24">
                <ScrollReveal variant="fade-up" distance={36} duration={800}>
                    <div className="bg-[#F5F5F5] rounded-3xl w-full overflow-hidden">
                        <div className="flex flex-col px-12 xl:px-16 py-10 xl:py-14">
                            {/* Heading */}
                            <ScrollReveal variant="blur-up" delay={100} duration={800}>
                                <h2 className="text-[48px] xl:text-[56px] font-extrabold leading-[2] tracking-tight mb-10 xl:mb-12">
                                    <span className="text-secondary">{t('podusFeatures.heading1')} </span>
                                    <span className="text-[#AEAEAE]">{t('podusFeatures.heading2')}</span>
                                </h2>
                            </ScrollReveal>

                            {/* Content Row */}
                            <div className="flex gap-10 xl:gap-16">
                                {/* Left: Numbered Feature List */}
                                <div className="w-[48%] flex-shrink-0">
                                    {features.map((feature, i) => {
                                        const isActive = activeIndex === i;
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                className="flex items-start gap-5 w-full text-left cursor-pointer"
                                                onClick={() => toggle(i)}
                                            >
                                                <span
                                                    className={`text-base font-bold min-w-[28px] text-right pt-[22px] transition-colors duration-300 ${
                                                        isActive ? 'text-secondary' : 'text-secondary-100'
                                                    }`}
                                                >
                                                    {feature.number}
                                                </span>

                                                <div
                                                    className={`flex-1 border-l-2 pl-6 py-5 transition-colors duration-300 ${
                                                        isActive ? 'border-secondary' : 'border-gray-300'
                                                    }`}
                                                >
                                                    <h3
                                                        className={`text-xl xl:text-2xl font-bold transition-colors duration-300 ${
                                                            isActive ? 'text-secondary' : 'text-[#AEAEAE]'
                                                        }`}
                                                    >
                                                        {t(`podusFeatures.${i}.title`)}
                                                    </h3>

                                                    {/* Animated expand/collapse */}
                                                    <div
                                                        className="grid"
                                                        style={{
                                                            gridTemplateRows: isActive ? '1fr' : '0fr',
                                                            transition: 'grid-template-rows 0.4s ease-out',
                                                        }}
                                                    >
                                                        <div className="overflow-hidden">
                                                            <p className="text-secondary-300 text-sm leading-[2] mt-3 max-w-lg">
                                                                {t(`podusFeatures.${i}.description`)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Right: Feature Image */}
                                <div className="flex-1 flex items-center justify-center min-h-[460px]">
                                    <img
                                        key={activeIndex}
                                        src={activeFeature.image}
                                        alt={t(`podusFeatures.${Math.max(0, activeIndex)}.title`)}
                                        className="max-h-[460px] w-full object-contain animate-fadeIn"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollReveal>
            </section>

            {/* ─── Mobile: Horizontal Snap Carousel ─── */}
            <section className="lg:hidden px-4 py-10">
                <div className="bg-[#F5F5F5] rounded-2xl px-5 py-8">
                    {/* Heading */}
                    <h2 className="text-[26px] sm:text-[32px] font-bold leading-[2] tracking-tight mb-6">
                        <span className="text-secondary">{t('podusFeatures.heading1')} </span>
                        <span className="text-[#AEAEAE]">{t('podusFeatures.heading2')}</span>
                    </h2>

                    <MobileFeatureAccordion />
                </div>
            </section>
        </>
    );
}
